import { Box, Text } from '@nimbus-ds/components';
import { TrashIcon } from '@nimbus-ds/icons';
import { DataList } from '@nimbus-ds/patterns';
import { MetaplanItemResponseDto } from '@tiendanube/common';
import { useToastProgress } from 'App/components/ToastProgressContext';
import { ActionIconButton, InternalNavLink } from 'commons/components';
import { useModal, useToast } from 'commons/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import {
  DeleteMetaplanModal,
  SubscriptionConditions,
} from 'domains/Catalog/Subscriptions/components';
import { useDeleteMetaplanMutation } from 'domains/Catalog/Subscriptions/subscriptionsApi';

interface MetaplansListRowProps {
  metaplan: MetaplanItemResponseDto;
  index: number;
}

export function MetaplansListMobileRow({
  metaplan,
  index,
}: Readonly<MetaplansListRowProps>) {
  const t = useTranslationCatalog();
  const [showDeleteModal, openDeleteModal, closeDeleteModal] = useModal();
  const [deleteMetaplan, { isLoading }] = useDeleteMetaplanMutation();
  const { addToast } = useToast();
  const { addToastProgress, closeToastProgress } = useToastProgress();

  const handleDelete = async (): Promise<void> => {
    closeDeleteModal();
    addToastProgress({
      label: t('subscriptions.deleteModal.progress'),
    });
    try {
      await deleteMetaplan(metaplan.id).unwrap();
      closeToastProgress();
      addToast({
        label: t('subscriptions.deleteModal.success'),
        appearance: 'success',
      });
    } catch {
      closeToastProgress();
      addToast({
        label: t('subscriptions.deleteModal.error'),
        appearance: 'danger',
      });
    }
  };

  return (
    <>
      <DataList.Row backgroundColor="transparent" topDivider={index !== 0}>
        <Box display="flex" flexDirection="column" alignItems="stretch" gap="3">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <InternalNavLink to={metaplan.id}>
              <Text color="primary-interactive" fontWeight="regular">
                {metaplan.name}
              </Text>
            </InternalNavLink>
            <Box display="flex" justifyContent="flex-end" gap="2">
              <ActionIconButton
                content={t('subscriptions.table.deleteMetaplan')}
                source={<TrashIcon />}
                onClick={openDeleteModal}
              />
            </Box>
          </Box>
          <SubscriptionConditions
            subscriptionOptions={metaplan.subscriptionOptions}
          />
        </Box>
      </DataList.Row>
      <DeleteMetaplanModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        isLoading={isLoading}
      />
    </>
  );
}
